"use client";
import React, { useRef, useState, useCallback } from "react";
import { cn } from "@/lib/utils";

interface CardSpotlightProps extends React.HTMLAttributes<HTMLDivElement> {
  radius?: number;
  color?: string;
}

export const CardSpotlight = React.forwardRef<HTMLDivElement, CardSpotlightProps>(
  ({ className, children, radius = 350, color, ...props }, forwardedRef) => {
    const internalRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isHovered, setIsHovered] = useState(false);

    const setRef = useCallback(
      (node: HTMLDivElement | null) => {
        (internalRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef)
          (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [forwardedRef]
    );

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      const div = internalRef.current;
      if (!div) return;
      const rect = div.getBoundingClientRect();
      setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };

    return (
      <div
        ref={setRef}
        className={cn(
          "group relative overflow-hidden rounded-xl border border-border bg-muted/30 transition-all duration-300 hover:border-primary/30",
          className
        )}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        {...props}
      >
        <div
          className="pointer-events-none absolute inset-0 motion-safe:transition-opacity motion-safe:duration-500"
          aria-hidden="true"
          style={{
            opacity: isHovered ? 1 : 0,
            background: `radial-gradient(${radius}px circle at ${position.x}px ${position.y}px, ${
              color ?? "hsl(var(--primary) / 0.1)"
            }, transparent 80%)`,
          }}
        />
        {children}
      </div>
    );
  }
);
CardSpotlight.displayName = "CardSpotlight";
